/**
 * ij-feedback.js — in-page feedback widget for the live site.
 *
 * Pattern ported from the cityside FeedbackWidget; rewritten as vanilla
 * JS so it can drop into any Shopify theme page without bundling.
 *
 * Activates when the page URL has `?feedback=1` OR a cookie `ij-feedback=1`
 * is present. Renders a floating pill bottom-right; click → point-and-
 * click any element on the page → leave a feedback note.
 *
 * Persistence: localStorage, keyed per-host. Two reader paths for Claude:
 *   - window.__IJ_FEEDBACK__.exportJson() → string of all threads
 *   - window.__IJ_FEEDBACK__.getOpen()   → array of open threads
 *
 * SECURITY NOTE: all DOM rendering uses createElement + textContent for
 * user-controlled fields (body, selector, target text). Only static
 * markup templates use innerHTML. This avoids XSS from feedback bodies
 * even though feedback only ships in localStorage (no server side).
 */

(function () {
  'use strict';

  // ============================================================
  // Activation gate
  // ============================================================
  const url = new URL(window.location.href);
  const queryEnabled = url.searchParams.get('feedback') === '1';
  const cookieEnabled =
    typeof document !== 'undefined' &&
    document.cookie.split('; ').some((c) => c.startsWith('ij-feedback=1'));

  if (!queryEnabled && !cookieEnabled) return;

  if (queryEnabled) {
    document.cookie = 'ij-feedback=1; Path=/; Max-Age=' + 60 * 60 * 24 * 7 + '; SameSite=Lax';
  }

  // ============================================================
  // Storage
  // ============================================================
  const STORAGE_KEY = 'ij-feedback:' + location.host;

  function load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { threads: [] };
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.threads)) return { threads: [] };
      return parsed;
    } catch (_) {
      return { threads: [] };
    }
  }

  function save(s) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    } catch (e) {
      console.warn('[ij-feedback] could not persist', e);
    }
  }

  function newId() {
    return 'tid_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
  }

  // ============================================================
  // Element identification
  // ============================================================

  function getSelector(el) {
    const path = [];
    let node = el;
    let depth = 0;
    while (node && node !== document.body && depth < 6) {
      if (node.id) {
        path.unshift('#' + CSS.escape(node.id));
        break;
      }
      let part = node.tagName.toLowerCase();
      const parent = node.parentElement;
      if (parent) {
        const sameTag = Array.from(parent.children).filter(
          (c) => c.tagName === node.tagName,
        );
        if (sameTag.length > 1) {
          part += ':nth-of-type(' + (sameTag.indexOf(node) + 1) + ')';
        }
      }
      path.unshift(part);
      node = parent;
      depth++;
    }
    return path.join(' > ') || el.tagName.toLowerCase();
  }

  function getXPath(el) {
    if (el.id) return '//*[@id="' + el.id + '"]';
    const parts = [];
    let node = el;
    while (node && node.nodeType === 1 && node !== document.body) {
      let index = 1;
      let sibling = node.previousSibling;
      while (sibling) {
        if (sibling.nodeType === 1 && sibling.nodeName === node.nodeName) index++;
        sibling = sibling.previousSibling;
      }
      parts.unshift(node.nodeName.toLowerCase() + '[' + index + ']');
      node = node.parentNode;
    }
    return '/html/body/' + parts.join('/');
  }

  function describeElement(el) {
    const r = el.getBoundingClientRect();
    const raw = (el.textContent || '').replace(/\s+/g, ' ').trim();
    return {
      selector: getSelector(el),
      xpath: getXPath(el),
      tagName: el.tagName,
      textPreview: raw.length > 100 ? raw.slice(0, 100) + '…' : raw,
      rect: {
        x: Math.round(r.left + window.scrollX),
        y: Math.round(r.top + window.scrollY),
        width: Math.round(r.width),
        height: Math.round(r.height),
      },
    };
  }

  function isInsideWidget(el) {
    if (!el) return true;
    return !!el.closest('[data-ij-feedback]');
  }

  // ============================================================
  // Styles
  // ============================================================
  const STYLE = `
    [data-ij-feedback] {
      box-sizing: border-box;
      font-family: -apple-system, BlinkMacSystemFont, "Inter", "Helvetica Neue", system-ui, sans-serif;
    }
    [data-ij-feedback] *, [data-ij-feedback] *::before, [data-ij-feedback] *::after { box-sizing: border-box; }
    body.ij-fb-mode { cursor: crosshair !important; }
    body.ij-fb-mode [data-ij-feedback], body.ij-fb-mode [data-ij-feedback] * { cursor: auto !important; }
    /* In commenting mode, force every element to accept pointer events so
       the reviewer can target ANYTHING — including decorative overlays
       (chain image, tutorial card, share modal, drag-hint chip, charm
       marker, etc.) that intentionally set pointer-events:none in
       normal use. Without this, mouseover/click events fall through to
       the parent and the reviewer can only select the outer container.
       The widget's own UI (data-ij-feedback subtree) is exempt — those
       elements need their normal pointer-events for the composer + side
       panel to keep working. */
    body.ij-fb-mode *:not([data-ij-feedback]):not([data-ij-feedback] *) {
      pointer-events: auto !important;
    }
    .ij-fb-hover {
      outline: 2px solid #9b7e4c !important;
      outline-offset: 3px !important;
      background-color: rgba(155, 126, 76, 0.05) !important;
      cursor: crosshair !important;
    }
    #ij-feedback-pill {
      position: fixed; right: 18px; bottom: 18px; z-index: 2147483647;
      display: inline-flex; align-items: center; gap: 8px;
      padding: 10px 16px 10px 14px;
      background: #1a1614; color: #faf8f4;
      border: 1px solid rgba(155, 126, 76, 0.5);
      border-radius: 999px;
      box-shadow: 0 8px 24px -8px rgba(0,0,0,0.45);
      font-size: 13px; font-weight: 500; letter-spacing: 0.04em;
      cursor: pointer; user-select: none;
      transition: transform 180ms ease, box-shadow 180ms ease, background 180ms ease;
    }
    #ij-feedback-pill:hover { transform: translateY(-1px); box-shadow: 0 12px 28px -8px rgba(0,0,0,0.55); }
    #ij-feedback-pill.is-active {
      background: #9b7e4c; border-color: #9b7e4c; color: #1a1614;
    }
    #ij-feedback-pill .ij-fb-pill-count {
      background: rgba(155,126,76,0.25); padding: 2px 7px; border-radius: 999px;
      font-size: 11px; font-weight: 600;
    }
    #ij-feedback-pill.is-active .ij-fb-pill-count { background: rgba(26,22,20,0.18); }

    #ij-feedback-composer {
      position: absolute; z-index: 2147483646;
      width: 340px; background: #faf8f4;
      border: 1px solid rgba(26,22,20,0.12); border-radius: 8px;
      box-shadow: 0 18px 38px -10px rgba(0,0,0,0.32), 0 4px 10px -4px rgba(0,0,0,0.18);
      padding: 12px;
      animation: ij-fb-pop 180ms cubic-bezier(0.23, 1, 0.32, 1);
    }
    @keyframes ij-fb-pop { from { opacity: 0; transform: translateY(4px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
    .ij-fb-composer-target {
      font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase;
      color: #8a847f; margin-bottom: 6px;
      word-break: break-all; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .ij-fb-composer-input {
      width: 100%; min-height: 76px; padding: 8px 10px;
      border: 1px solid rgba(26,22,20,0.18); border-radius: 4px;
      background: #fff; color: #1a1614; font: inherit; font-size: 13px; line-height: 1.45;
      resize: vertical; outline: none;
    }
    .ij-fb-composer-input:focus { border-color: #9b7e4c; }
    .ij-fb-composer-actions { display: flex; gap: 6px; justify-content: flex-end; margin-top: 8px; }

    .ij-fb-btn {
      padding: 7px 14px; border-radius: 4px; font: inherit; font-size: 12px; font-weight: 500;
      letter-spacing: 0.04em; border: 1px solid transparent; cursor: pointer;
      transition: background 160ms ease, border-color 160ms ease;
    }
    .ij-fb-btn--ghost { background: transparent; color: #4a4441; border-color: rgba(26,22,20,0.18); }
    .ij-fb-btn--ghost:hover { background: rgba(26,22,20,0.04); }
    .ij-fb-btn--primary { background: #1a1614; color: #faf8f4; }
    .ij-fb-btn--primary:hover { background: #2e2826; }
    .ij-fb-btn--primary:disabled { opacity: 0.4; cursor: not-allowed; }

    #ij-feedback-panel {
      position: fixed; top: 0; right: 0; bottom: 0;
      width: 380px; max-width: 92vw; z-index: 2147483645;
      background: #faf8f4; border-left: 1px solid rgba(26,22,20,0.12);
      box-shadow: -18px 0 38px -10px rgba(0,0,0,0.18);
      display: flex; flex-direction: column;
      transform: translateX(100%);
      transition: transform 240ms cubic-bezier(0.23, 1, 0.32, 1);
    }
    #ij-feedback-panel.is-open { transform: translateX(0); }
    .ij-fb-panel-head {
      padding: 16px 18px; border-bottom: 1px solid rgba(26,22,20,0.1);
      display: flex; align-items: center; justify-content: space-between;
    }
    .ij-fb-panel-title {
      font-family: 'Didot', 'Playfair Display', Georgia, serif;
      font-style: italic; font-size: 22px; color: #1a1614; margin: 0;
    }
    .ij-fb-panel-close {
      background: transparent; border: 0;
      width: 28px; height: 28px; border-radius: 50%;
      cursor: pointer; font-size: 18px; color: #4a4441;
      display: inline-flex; align-items: center; justify-content: center;
    }
    .ij-fb-panel-close:hover { background: rgba(26,22,20,0.06); }
    .ij-fb-panel-body { flex: 1; overflow-y: auto; padding: 8px 14px 24px; }

    .ij-fb-thread {
      border: 1px solid rgba(26,22,20,0.1); border-radius: 6px;
      padding: 12px 14px; margin: 10px 0; background: #fff;
      cursor: default; transition: border-color 160ms ease;
    }
    .ij-fb-thread:hover { border-color: rgba(155,126,76,0.5); }
    .ij-fb-thread.is-resolved { opacity: 0.55; background: #f3efe7; }
    .ij-fb-thread-meta {
      display: flex; align-items: center; gap: 8px;
      font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
      color: #8a847f; margin-bottom: 6px;
    }
    .ij-fb-thread-status {
      display: inline-block; padding: 1px 7px; border-radius: 99px;
      font-size: 9.5px; font-weight: 600; letter-spacing: 0.18em;
    }
    .ij-fb-thread-status.open { background: rgba(155,126,76,0.18); color: #6b5230; }
    .ij-fb-thread-status.resolved { background: rgba(26,22,20,0.08); color: #4a4441; }
    .ij-fb-thread-target { font-size: 11px; color: #4a4441; margin: 4px 0 6px; font-style: italic; word-break: break-all; }
    .ij-fb-thread-body { font-size: 13.5px; line-height: 1.5; color: #1a1614; white-space: pre-wrap; }
    .ij-fb-thread-actions {
      display: flex; gap: 6px; margin-top: 10px; padding-top: 8px;
      border-top: 1px dashed rgba(26,22,20,0.12);
    }
    .ij-fb-empty {
      padding: 32px 14px; text-align: center; color: #8a847f;
      font-style: italic; font-family: 'Didot', 'Playfair Display', Georgia, serif;
    }
    .ij-fb-export-row {
      padding: 12px 18px; border-top: 1px solid rgba(26,22,20,0.1);
      display: flex; gap: 6px; flex-wrap: wrap;
    }
    .ij-fb-export-row .ij-fb-btn { font-size: 11px; padding: 6px 12px; }

    @media (max-width: 600px) {
      #ij-feedback-pill { right: 12px; bottom: 12px; padding: 8px 14px 8px 12px; font-size: 12px; }
      #ij-feedback-composer { width: 92vw; }
      #ij-feedback-panel { width: 100vw; max-width: 100vw; }
    }
  `;

  function injectStyle() {
    if (document.getElementById('ij-feedback-style')) return;
    const tag = document.createElement('style');
    tag.id = 'ij-feedback-style';
    tag.textContent = STYLE;
    document.head.appendChild(tag);
  }

  // ============================================================
  // Safe-DOM helpers — build trees with createElement, never innerHTML
  // for user-supplied content.
  // ============================================================
  function el(tag, props, ...children) {
    const node = document.createElement(tag);
    if (props) {
      for (const k of Object.keys(props)) {
        const v = props[k];
        if (k === 'class') node.className = v;
        else if (k === 'text') node.textContent = v;
        else if (k === 'data') {
          for (const dk of Object.keys(v)) node.dataset[dk] = v[dk];
        } else if (k === 'on') {
          for (const ek of Object.keys(v)) node.addEventListener(ek, v[ek]);
        } else if (k === 'style') {
          for (const sk of Object.keys(v)) node.style[sk] = v[sk];
        } else if (k === 'title') node.title = v;
        else if (k === 'type') node.type = v;
        else if (k === 'placeholder') node.placeholder = v;
        else if (k === 'disabled') node.disabled = v;
        else if (k === 'ariaLabel') node.setAttribute('aria-label', v);
        else node.setAttribute(k, v);
      }
    }
    for (const c of children) {
      if (c == null || c === false) continue;
      node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    }
    return node;
  }

  // ============================================================
  // State
  // ============================================================
  const state = {
    mode: 'idle',
    state: load(),
    composerEl: null,
    composerTargetEl: null,
    composerTargetRef: null,
    hoveredEl: null,
    panelOpen: false,
  };

  function unsetHover() {
    if (state.hoveredEl) {
      state.hoveredEl.classList.remove('ij-fb-hover');
      state.hoveredEl = null;
    }
  }
  function setHover(node) {
    if (state.hoveredEl === node) return;
    unsetHover();
    if (!node) return;
    node.classList.add('ij-fb-hover');
    state.hoveredEl = node;
  }

  // ─── Floating pill ───────────────────────────────────────
  let pillEl;
  let pillLabelEl;
  let pillCountEl;
  function renderPill() {
    if (!pillEl) {
      pillLabelEl = el('span', { class: 'ij-fb-pill-label' }, 'Leave feedback');
      pillCountEl = el('span', { class: 'ij-fb-pill-count' }, '0');
      pillEl = el(
        'button',
        {
          id: 'ij-feedback-pill',
          type: 'button',
          'data-ij-feedback': '',
          on: {
            click: onPillClick,
            contextmenu: (e) => {
              e.preventDefault();
              togglePanel(true);
            },
          },
        },
        el('span', { 'aria-hidden': 'true' }, '✎'),
        pillLabelEl,
        pillCountEl,
      );
      document.body.appendChild(pillEl);
    }
    pillEl.classList.toggle('is-active', state.mode === 'commenting');
    const openCount = state.state.threads.filter((t) => t.status === 'open').length;
    pillCountEl.textContent = String(openCount);
    pillLabelEl.textContent =
      state.mode === 'commenting' ? 'Click an element' : 'Leave feedback';
  }

  function onPillClick() {
    if (state.mode === 'commenting') {
      state.mode = 'idle';
      unsetHover();
      hideComposer();
    } else {
      state.mode = 'commenting';
      togglePanel(true);
    }
    document.body.classList.toggle('ij-fb-mode', state.mode === 'commenting');
    renderPill();
  }

  // ─── Hover + click capture ───────────────────────────────
  document.addEventListener('mouseover', (e) => {
    if (state.mode !== 'commenting' || state.composerEl) return;
    const t = e.target;
    if (!t || isInsideWidget(t)) {
      unsetHover();
      return;
    }
    setHover(t);
  });

  document.addEventListener(
    'click',
    (e) => {
      if (state.mode !== 'commenting') return;
      const t = e.target;
      if (!t || isInsideWidget(t)) return;
      e.preventDefault();
      e.stopPropagation();
      openComposerFor(t);
    },
    true,
  );

  /* Pointerdown + mousedown capture — in commenting mode, stop these
     from bubbling to app-level handlers. Without this, native
     pointerdown can kick off framer-motion drag gestures on the
     canvas's `.ij__marker` charms (or any other interactive child),
     so what the reviewer intended as "click to give feedback on this
     charm" becomes "start dragging this charm."

     IMPORTANT: stopPropagation only — NEVER preventDefault. Calling
     preventDefault on mousedown suppresses the subsequent `click`
     event browser-side, which we NEED in the click handler above to
     open the composer. stopPropagation alone blocks the canvas's
     event listeners (they're bubble-phase) while leaving the native
     click event sequence intact. */
  document.addEventListener(
    'pointerdown',
    (e) => {
      if (state.mode !== 'commenting') return;
      const t = e.target;
      if (!t || isInsideWidget(t)) return;
      e.stopPropagation();
    },
    true,
  );
  document.addEventListener(
    'mousedown',
    (e) => {
      if (state.mode !== 'commenting') return;
      const t = e.target;
      if (!t || isInsideWidget(t)) return;
      e.stopPropagation();
    },
    true,
  );

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (state.composerEl) {
      hideComposer();
    } else if (state.mode === 'commenting') {
      state.mode = 'idle';
      document.body.classList.remove('ij-fb-mode');
      unsetHover();
      renderPill();
    } else if (state.panelOpen) {
      togglePanel(false);
    }
  });

  // ─── Composer popover ────────────────────────────────────
  function openComposerFor(targetEl) {
    const ref = describeElement(targetEl);
    state.composerTargetEl = targetEl;
    state.composerTargetRef = ref;
    unsetHover();

    if (state.composerEl) state.composerEl.remove();

    const targetLabel = el(
      'div',
      { class: 'ij-fb-composer-target', title: ref.selector },
      ref.tagName.toLowerCase() + ' · ' + (ref.textPreview || ref.selector),
    );
    const input = el('textarea', {
      class: 'ij-fb-composer-input',
      placeholder: 'Describe what you want changed about this…',
    });
    const cancelBtn = el(
      'button',
      { type: 'button', class: 'ij-fb-btn ij-fb-btn--ghost', on: { click: hideComposer } },
      'Cancel',
    );
    const submitBtn = el(
      'button',
      {
        type: 'button',
        class: 'ij-fb-btn ij-fb-btn--primary',
        disabled: true,
        on: { click: submit },
      },
      'Send',
    );

    input.addEventListener('input', () => {
      submitBtn.disabled = input.value.trim().length === 0;
    });
    input.addEventListener('keydown', (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        if (!submitBtn.disabled) submit();
      }
    });

    const composer = el(
      'div',
      { id: 'ij-feedback-composer', 'data-ij-feedback': '' },
      targetLabel,
      input,
      el('div', { class: 'ij-fb-composer-actions' }, cancelBtn, submitBtn),
    );
    document.body.appendChild(composer);
    state.composerEl = composer;

    // Position
    const composerW = 340;
    const composerH = composer.offsetHeight || 200;
    const r = targetEl.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const gap = 10;
    const spaceBelow = vh - r.bottom;
    const spaceAbove = r.top;
    const placement =
      spaceBelow >= composerH + gap || spaceBelow >= spaceAbove ? 'below' : 'above';
    let top =
      placement === 'below'
        ? r.bottom + gap + window.scrollY
        : r.top - composerH - gap + window.scrollY;
    let left = r.left + r.width / 2 - composerW / 2 + window.scrollX;
    left = Math.max(16, Math.min(vw - composerW - 16, left));
    composer.style.top = top + 'px';
    composer.style.left = left + 'px';
    setTimeout(() => input.focus(), 50);

    function submit() {
      const body = input.value.trim();
      if (!body) return;
      const thread = {
        id: newId(),
        url: location.href,
        createdAt: new Date().toISOString(),
        status: 'open',
        element: state.composerTargetRef,
        body,
      };
      state.state.threads.unshift(thread);
      save(state.state);
      hideComposer();
      state.mode = 'idle';
      document.body.classList.remove('ij-fb-mode');
      renderPill();
      renderPanel();
      togglePanel(true);
    }
  }

  function hideComposer() {
    if (state.composerEl) {
      state.composerEl.remove();
      state.composerEl = null;
    }
    state.composerTargetEl = null;
    state.composerTargetRef = null;
  }

  // ─── Side panel ──────────────────────────────────────────
  let panelEl;
  let panelBodyEl;
  function ensurePanel() {
    if (panelEl) return panelEl;

    panelBodyEl = el('div', { class: 'ij-fb-panel-body' });

    const exportRow = el(
      'div',
      { class: 'ij-fb-export-row' },
      el(
        'button',
        {
          type: 'button',
          class: 'ij-fb-btn ij-fb-btn--ghost',
          on: {
            click: () => {
              const json = JSON.stringify(state.state, null, 2);
              if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(json).then(
                  () => alert('Feedback JSON copied — paste it to Claude.'),
                  () => prompt('Copy this JSON:', json),
                );
              } else {
                prompt('Copy this JSON:', json);
              }
            },
          },
        },
        'Copy JSON',
      ),
      el(
        'button',
        {
          type: 'button',
          class: 'ij-fb-btn ij-fb-btn--ghost',
          on: {
            click: () => {
              const blob = new Blob([JSON.stringify(state.state, null, 2)], {
                type: 'application/json',
              });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download =
                'ij-feedback-' +
                new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-') +
                '.json';
              document.body.appendChild(a);
              a.click();
              a.remove();
            },
          },
        },
        'Download',
      ),
      el(
        'button',
        {
          type: 'button',
          class: 'ij-fb-btn ij-fb-btn--ghost',
          on: {
            click: () => {
              if (!confirm('Clear ALL feedback threads? This cannot be undone.')) return;
              state.state = { threads: [] };
              save(state.state);
              renderPanel();
              renderPill();
            },
          },
        },
        'Clear all',
      ),
    );

    panelEl = el(
      'aside',
      { id: 'ij-feedback-panel', 'data-ij-feedback': '' },
      el(
        'div',
        { class: 'ij-fb-panel-head' },
        el('h3', { class: 'ij-fb-panel-title' }, 'Feedback'),
        el(
          'button',
          {
            type: 'button',
            class: 'ij-fb-panel-close',
            ariaLabel: 'Close',
            on: { click: () => togglePanel(false) },
          },
          '×',
        ),
      ),
      panelBodyEl,
      exportRow,
    );
    document.body.appendChild(panelEl);
    return panelEl;
  }

  function togglePanel(open) {
    ensurePanel();
    state.panelOpen = !!open;
    panelEl.classList.toggle('is-open', state.panelOpen);
    if (state.panelOpen) renderPanel();
  }

  function renderPanel() {
    ensurePanel();
    // Clear body
    while (panelBodyEl.firstChild) panelBodyEl.removeChild(panelBodyEl.firstChild);

    const threads = state.state.threads;
    if (threads.length === 0) {
      const empty = el('div', { class: 'ij-fb-empty' });
      empty.appendChild(document.createTextNode('No feedback yet.'));
      empty.appendChild(el('br'));
      empty.appendChild(document.createTextNode('Click "Leave feedback" then point at any element.'));
      panelBodyEl.appendChild(empty);
      return;
    }

    threads.forEach((t) => {
      const status = el('span', { class: 'ij-fb-thread-status ' + t.status }, t.status);
      const meta = el(
        'div',
        { class: 'ij-fb-thread-meta' },
        status,
        el('span', null, new Date(t.createdAt).toLocaleString()),
      );

      const targetLine = el(
        'div',
        { class: 'ij-fb-thread-target', title: t.element.selector },
        t.element.tagName.toLowerCase() +
          ' · ' +
          (t.element.textPreview || t.element.selector).slice(0, 80),
      );

      const bodyLine = el('div', { class: 'ij-fb-thread-body', text: t.body });

      const scrollBtn = el(
        'button',
        {
          type: 'button',
          class: 'ij-fb-btn ij-fb-btn--ghost',
          on: {
            click: () => {
              const node = document.querySelector(t.element.selector);
              if (node) {
                node.scrollIntoView({ behavior: 'smooth', block: 'center' });
                node.classList.add('ij-fb-hover');
                setTimeout(() => node.classList.remove('ij-fb-hover'), 1200);
              } else {
                window.scrollTo({ top: t.element.rect.y - 80, behavior: 'smooth' });
              }
            },
          },
        },
        'Scroll to',
      );
      const toggleBtn = el(
        'button',
        {
          type: 'button',
          class: 'ij-fb-btn ij-fb-btn--ghost',
          on: {
            click: () => {
              t.status = t.status === 'open' ? 'resolved' : 'open';
              save(state.state);
              renderPanel();
              renderPill();
            },
          },
        },
        t.status === 'open' ? 'Mark resolved' : 'Reopen',
      );
      const deleteBtn = el(
        'button',
        {
          type: 'button',
          class: 'ij-fb-btn ij-fb-btn--ghost',
          on: {
            click: () => {
              state.state.threads = state.state.threads.filter((x) => x.id !== t.id);
              save(state.state);
              renderPanel();
              renderPill();
            },
          },
        },
        'Delete',
      );
      const actions = el('div', { class: 'ij-fb-thread-actions' }, scrollBtn, toggleBtn, deleteBtn);

      const thread = el(
        'div',
        {
          class: 'ij-fb-thread' + (t.status === 'resolved' ? ' is-resolved' : ''),
          data: { threadId: t.id },
        },
        meta,
        targetLine,
        bodyLine,
        actions,
      );
      panelBodyEl.appendChild(thread);
    });
  }

  // ─── Cross-tab sync ──────────────────────────────────────
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    state.state = load();
    renderPill();
    if (state.panelOpen) renderPanel();
  });

  // ============================================================
  // Public API
  // ============================================================
  window.__IJ_FEEDBACK__ = {
    getAll() {
      return load().threads.slice();
    },
    getOpen() {
      return load().threads.filter((t) => t.status === 'open');
    },
    getResolved() {
      return load().threads.filter((t) => t.status === 'resolved');
    },
    exportJson() {
      return JSON.stringify(load(), null, 2);
    },
    clear() {
      save({ threads: [] });
      state.state = { threads: [] };
      renderPill();
      if (state.panelOpen) renderPanel();
    },
    open() {
      togglePanel(true);
    },
    locate(threadId) {
      const t = load().threads.find((x) => x.id === threadId);
      if (!t) return null;
      const node = document.querySelector(t.element.selector);
      if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return { selector: t.element.selector, found: !!node };
    },
    setStatus(threadId, status) {
      const s = load();
      const t = s.threads.find((x) => x.id === threadId);
      if (!t) return false;
      t.status = status === 'resolved' ? 'resolved' : 'open';
      save(s);
      state.state = s;
      renderPill();
      if (state.panelOpen) renderPanel();
      return true;
    },
  };

  // ─── Boot ────────────────────────────────────────────────
  function boot() {
    injectStyle();
    renderPill();
    ensurePanel();
    renderPanel();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
